<template>
    <div class="l-inline l-gap-3 notification-details-card" v-on="$listeners">
        <AlarmBellSleepIcon v-if="snoozedUntil" width="28" height="28" />

        <AlarmBellIcon v-else width="28" height="28" />

        <div class="l-stack l-gap-1 notification-details-card__info">
            <div>
                <h2 class="l-inline l-center-v">
                    <span>{{ rule.name }}</span>

                    <Badge v-if="snoozedUntil">
                        {{ $t('snoozedUntil', [snoozedUntil]) }}
                    </Badge>

                    <ActiveInactiveBadge v-else :active="rule.active">
                        {{ $t(rule.active ? 'active' : 'inactive') }}
                    </ActiveInactiveBadge>

                    <Badge v-if="rule.delay > 0">
                        {{ $t('delayed') }}
                    </Badge>
                </h2>
            </div>

            <p class="notification-details-card__description">
                <i>{{ $t('if') }}</i>

                <span
                    v-for="(condition, i) in conditions"
                    :key="'condition-' + i"
                >
                    <b v-if="condition.type === 'geofence'">
                        {{ $t(condition.position) }}
                        {{ getLocationName(condition.locationId) }}
                    </b>

                    <b v-else-if="condition.type === 'maintenance'">
                        {{ $t('maintenancePolicy') }}
                    </b>

                    <b v-else-if="condition.type === 'regex'">
                        <template v-if="condition.value === 'battery_status'">
                            {{ $t(`shared.measurements.${condition.value}`) }}
                            =
                            {{
                                $t(
                                    `shared.measurementOptions.${condition.value}.${condition.regex}`
                                )
                            }}
                        </template>

                        <template v-else>
                            {{
                                $t('regexMatches', [
                                    condition.value,
                                    condition.regex,
                                ])
                            }}
                        </template>
                    </b>

                    <b v-else-if="condition.type === 'state'">
                        {{ $t(condition.value) }}
                        {{ $t(condition.state ? 'isOn' : 'isOff') }}
                    </b>

                    <b v-else-if="condition.type === 'offline'">
                        {{
                            $t('offlinefor', {
                                amount:
                                    condition.seconds /
                                    timeOptions[condition.user_unit],
                                unit: condition.user_unit,
                            })
                        }}
                    </b>

                    <b v-else-if="condition.type === 'online'">
                        {{
                            $t('onlineWithin', {
                                amount:
                                    condition.seconds /
                                    timeOptions[condition.user_unit],
                                unit: condition.user_unit,
                            })
                        }}
                    </b>

                    <b v-else-if="condition.type === 'threshold'">
                        <template v-if="condition.value === 'button_mode'">
                            {{
                                $t(
                                    `shared.measurementOptions.${condition.value}.${condition.threshold}`
                                )
                            }}
                        </template>

                        <template
                            v-else-if="
                                condition.value ===
                                    'driver.current_daily_driving_time'
                            "
                        >
                            {{ getMeasurementTranslation(condition.value) }}
                        </template>

                        <template v-else-if="condition.value === 'dtc_codes'">
                            {{ $t('dtcCodesAvailable') }}
                        </template>

                        <template
                            v-else-if="condition.value === 'ev_charging_state'"
                        >
                            {{ $t(`shared.measurements.${condition.value}`) }}
                            {{ $t(condition.state ? 'isOn' : 'isOff') }}
                        </template>

                        <template v-else>
                            {{ getMeasurementTranslation(condition.value) }}
                            {{ condition.operator }}
                            {{ condition.threshold }}
                            {{ units[condition.value] }}
                        </template>
                    </b>

                    <b v-else-if="condition.type === 'timedelta'">
                        <template
                            v-if="
                                condition.value_name ===
                                    'tachograph_next_calibration'
                            "
                        >
                            {{
                                $t(
                                    `shared.measurements.${condition.value_name}`
                                )
                            }}
                            {{
                                $tc(
                                    'timePrior',
                                    Math.round(condition.threshold / 60 / 60)
                                )
                            }}
                        </template>
                    </b>

                    <template v-else-if="condition.type === 'weekday_schedule'">
                        <b v-for="(item, k) in condition.weekdays" :key="k">
                            {{ $t(`shared.weekdaysShort.${item.weekday}`) }}
                            {{ item.start }} - {{ item.end }}
                            <span v-if="k < condition.weekdays.length - 1">
                                /
                            </span>
                        </b>
                    </template>

                    <i v-if="i < conditions.length - 1">
                        {{ $t(rule.conditions.matchMode + 'MatchMode') }}
                    </i>
                </span>

                <i>{{ $t('then') }}</i>

                <span
                    v-for="(value, group, i) in notificationChannelsGrouped"
                    :key="'channel-' + group"
                >
                    <i v-if="i > 0">
                        {{ $t('allMatchMode') }}
                    </i>

                    <b>
                        {{ $t('channel-' + group) }}
                    </b>

                    <i>({{ value }})</i>
                </span>

                <i
                    v-if="
                        Object.keys(notificationChannelsGrouped).length &&
                            (rule.alert_by_email || rule.alert_by_sms)
                    "
                >
                    {{ $t('allMatchMode') }}
                </i>

                <b v-if="rule.alert_by_email"> {{ $t('email to me') }}</b>

                <i v-if="rule.alert_by_email && rule.alert_by_sms">
                    {{ $t('allMatchMode') }}
                </i>

                <b v-if="rule.alert_by_sms"> {{ $t('sms to me') }}</b>
            </p>

            <p>
                <Pill v-if="rule.assets.length || rule.all_assets">
                    {{ $tc(`shared.assetCount`, rule.asset_count) }}
                </Pill>

                <Pill v-if="rule.locations.length">
                    {{ $tc('locations', rule.locations.length) }}
                </Pill>

                <Pill v-if="rule.asset_types.length">
                    {{ $tc('types', rule.asset_types.length) }}
                </Pill>
            </p>
        </div>

        <div
            class="l-inline l-start l-gap-2 notification-details-card__actions"
        >
            <label
                v-if="!snoozedUntil"
                class="clickable"
                @click="$event.stopPropagation()"
            >
                <ToggleButton
                    class="redesigned"
                    :value="rule.active"
                    sync
                    css-colors
                    :width="40"
                    :height="24"
                    :speed="150"
                    @input="$emit('toggle')"
                />

                <p>
                    {{ $t(rule.active ? 'labelDeactivate' : 'labelActivate') }}
                </p>
            </label>

            <IconButton
                :label="$t('labelResolve')"
                @click.stop="$emit('resolve')"
            >
                <EmailCheckIcon width="24" height="24" />
            </IconButton>

            <div>
                <IconButton
                    :label="$t(snoozedUntil ? 'labelUnsnooze' : 'labelSnooze')"
                    @click.stop="handleContextMenuOpen"
                >
                    <AlarmBellTimerIcon width="24" height="24" />
                </IconButton>

                <ContextMenu
                    ref="contextMenu"
                    :element-id="`notificationDetailsCardContextMenu${_uid}`"
                    :options="snoozeOptions"
                    @option-clicked="handleContextMenuClick"
                />
            </div>

            <IconButton :label="$t('labelCopy')" @click.stop="$emit('copy')">
                <CopyIcon width="24" height="24" />
            </IconButton>

            <IconButton
                :label="$t('labelDelete')"
                @click.stop="$emit('delete')"
            >
                <TrashBinIcon width="24" height="24" />
            </IconButton>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import ContextMenu from 'vue-simple-context-menu'
import { ToggleButton } from 'vue-js-toggle-button'

import { measurementHelper } from '@/utils'
import ActiveInactiveBadge from '../redesigned/ActiveInactiveBadge'
import AlarmBellIcon from '../icons/AlarmBellIcon'
import AlarmBellSleepIcon from '../icons/AlarmBellSleepIcon'
import AlarmBellTimerIcon from '../icons/AlarmBellTimerIcon'
import Badge from '../redesigned/Badge'
import CopyIcon from '../icons/CopyIcon'
import EmailCheckIcon from '../icons/EmailCheckIcon'
import IconButton from '../IconButton'
import Pill from '../redesigned/Pill'
import TrashBinIcon from '../icons/TrashBinIcon'

export default {
    name: 'NotificationDetailsCard',
    components: {
        ActiveInactiveBadge,
        AlarmBellIcon,
        AlarmBellSleepIcon,
        AlarmBellTimerIcon,
        Badge,
        ContextMenu,
        CopyIcon,
        EmailCheckIcon,
        IconButton,
        Pill,
        ToggleButton,
        TrashBinIcon,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            units: measurementHelper.units,
            timeOptions: {
                h: 60 * 60,
                min: 60,
            },
        }
    },
    computed: {
        ...mapState('location', ['locations']),
        ...mapState('tracker', ['trackers']),
        conditions() {
            return this.rule.conditions.conditions.map(condition => {
                let { threshold } = condition
                if (condition.value === 'running_time') {
                    threshold = (condition.threshold / 3600).toFixed(2)
                } else if (measurementHelper.converters[condition.value]) {
                    threshold = measurementHelper.converters[condition.value](
                        condition.threshold
                    )
                }
                return { ...condition, threshold }
            })
        },
        notificationChannelsGrouped() {
            const email = this.rule.notification_channels
                .filter(item => item.type === 'EMAIL')
                .map(item => item.target)
                .join(', ')
            const sms = this.rule.notification_channels
                .filter(item => item.type === 'SMS')
                .map(item => item.target)
                .join(', ')
            const webhooks = this.rule.webhooks
                .map(item => item.description)
                .join(', ')
            return {
                ...(email.length && { email }),
                ...(sms.length && { sms }),
                ...(webhooks.length && { webhooks }),
            }
        },
        snoozeOptions() {
            if (this.snoozedUntil) {
                return [{ name: this.$t('activate'), value: 0 }]
            }

            return [
                { name: this.$t('snoozeOption5m'), value: 1000 * 60 * 5 },
                { name: this.$t('snoozeOption1h'), value: 1000 * 60 * 60 },
                { name: this.$t('snoozeOption4h'), value: 1000 * 60 * 60 * 4 },
                { name: this.$t('snoozeOption1d'), value: 1000 * 60 * 60 * 24 },
                {
                    name: this.$t('snoozeOption1w'),
                    value: 1000 * 60 * 60 * 24 * 7,
                },
            ]
        },
        snoozedUntil() {
            const datetime = moment(this.rule.valid_after)
            return datetime > moment.now()
                ? datetime.format('DD.MM.YYYY HH:mm')
                : null
        },
    },
    methods: {
        getLocationName(locationId) {
            return this.locations.find(location => location.id === locationId)
                ?.name
        },
        getMeasurementTranslation(measurement) {
            const translationKey = `shared.measurements.${measurement}`
            return this.$root.$te(translationKey)
                ? this.$t(translationKey)
                : measurement
        },
        handleContextMenuOpen(e) {
            this.$refs.contextMenu.showMenu(e)
        },
        handleContextMenuClick({ option: { value } }) {
            this.$emit('snooze', value)
        },
    },
}
</script>

<i18n>
{
    "en": {
        "activate": "Activate",
        "active": "Active",
        "allMatchMode": "and",
        "anyMatchMode": "or",
        "channel-email": "Send E-Mail",
        "channel-sms": "Send SMS",
        "channel-webhooks": "Trigger Webhook",
        "d1": "Channel 1",
        "d2": "Channel 2",
        "d3": "Channel 3",
        "d4": "Channel 4",
        "d5": "Channel 5",
        "d6": "Channel 6",
        "delayed": "Delayed",
        "dtcCodesAvailable": "DTC Code available",
        "email to me": "E-Mail to me",
        "if": "If",
        "inactive": "Inactive",
        "inside": "Inside",
        "isOff": "is Off",
        "isOn": "is On",
        "labelActivate": "Activate rule",
        "labelCopy": "Copy rule",
        "labelDeactivate": "Deactivate rule",
        "labelDelete": "Delete rule",
        "labelResolve": "Resolve notifications",
        "labelSnooze": "Snooze notifications",
        "labelUnsnooze": "End snooze",
        "locations": "{n} Location | {n} Locations",
        "maintenancePolicy": "Maintenance is needed",
        "offlinefor": "Offline for {amount} {unit}",
        "onlineWithin": "Online within the last {amount} {unit}",
        "outside": "Outside",
        "regexMatches": "{0} matches {1}",
        "sms to me": "SMS to me",
        "snoozeOption1d": "1 day",
        "snoozeOption1h": "1 hour",
        "snoozeOption1w": "1 week",
        "snoozeOption4h": "4 hours",
        "snoozeOption5m": "5 minutes",
        "snoozedUntil": "Snoozed until {0}",
        "then": "then",
        "timePrior": "in {n} hour | in {n} hours",
        "types": "{n} Type | {n} Types"
    },
    "de": {
        "activate": "Aktivieren",
        "active": "Aktiv",
        "allMatchMode": "und",
        "anyMatchMode": "oder",
        "channel-email": "E-Mail senden",
        "channel-sms": "SMS senden",
        "channel-webhooks": "Webhook Auslösen",
        "d1": "Kanal 1",
        "d2": "Kanal 2",
        "d3": "Kanal 3",
        "d4": "Kanal 4",
        "d5": "Kanal 5",
        "d6": "Kanal 6",
        "delayed": "Verzögert",
        "dtcCodesAvailable": "DTC Code vorhanden",
        "email to me": "E-Mail an mich",
        "if": "Wenn",
        "inactive": "Inaktiv",
        "inside": "Innerhalb",
        "isOff": "aus",
        "isOn": "ein",
        "labelActivate": "Regel aktivieren",
        "labelCopy": "Regel kopieren",
        "labelDeactivate": "Regel deaktivieren",
        "labelDelete": "Regel löschen",
        "labelResolve": "Benachrichtigungen auflösen",
        "labelSnooze": "Regel pausieren",
        "labelUnsnooze": "Pausierung aufheben",
        "locations": "{n} Standort | {n} Standorte",
        "maintenancePolicy": "Wartung ist erforderlich",
        "offlinefor": "Offline für {amount} {unit}",
        "onlineWithin": "Online innerhalb der letzten {amount} {unit}",
        "outside": "Ausserhalb",
        "regexMatches": "{0} matches {1}",
        "sms to me": "SMS an mich",
        "snoozeOption1d": "1 Tag",
        "snoozeOption1h": "1 Stunde",
        "snoozeOption1w": "1 Woche",
        "snoozeOption4h": "4 Stunden",
        "snoozeOption5m": "5 Minuten",
        "snoozedUntil": "Pausiert bis {0}",
        "then": "dann",
        "timePrior": "in {n} Stunde | in {n} Stunden",
        "types": "{n} Typ | {n} Typen"
    },
    "fr": {
        "activate": "Activer",
        "active": "Active",
        "allMatchMode": "et",
        "anyMatchMode": "ou",
        "channel-email": "Envoyer un e-mail",
        "channel-sms": "Envoyer un SMS",
        "channel-webhooks": "Déclencher Webhook",
        "d1": "Canal 1",
        "d2": "Canal 2",
        "d3": "Canal 3",
        "d4": "Canal 4",
        "d5": "Canal 5",
        "d6": "Canal 6",
        "delayed": "Retardé",
        "dtcCodesAvailable": "DTC Code disponible",
        "email to me": "E-mail à moi-même",
        "if": "Si",
        "inactive": "Inactive",
        "inside": "A l'intérieur de",
        "isOff": "est Off",
        "isOn": "est On",
        "labelActivate": "Activer la règle",
        "labelCopy": "Copier la règle",
        "labelDeactivate": "Désactiver la règle",
        "labelDelete": "Supprimer la règle",
        "labelResolve": "Résoudre les notifications",
        "labelSnooze": "Mettre la règle en pause",
        "labelUnsnooze": "Annuler la mise en pause",
        "locations": "{n} Emplacement | {n} Emplacements",
        "maintenancePolicy": "Une maintenance est nécessaire",
        "offlinefor": "Offline pour {amount} {unit}",
        "onlineWithin": "Online au cours des dernières {amount} {unit}",
        "outside": "A l'extérieur de",
        "regexMatches": "{0} matches {1}",
        "sms to me": "SMS à moi-même",
        "snoozeOption1d": "1 jour",
        "snoozeOption1h": "1 heure",
        "snoozeOption1w": "1 semaine",
        "snoozeOption4h": "4 heures",
        "snoozeOption5m": "5 minutes",
        "snoozedUntil": "Snoozed jusqu'à",
        "then": "alors",
        "timePrior": "dans {n} heure | dans {n} heures",
        "types": "{n} Type | {n} Types"
    },
    "it": {
        "activate": "Attivare",
        "active": "Attivo",
        "allMatchMode": "e",
        "anyMatchMode": "o",
        "channel-email": "Inviare E-Mail",
        "channel-sms": "Inviare SMS",
        "channel-webhooks": "Innesco Webhook",
        "d1": "Channel 1",
        "d2": "Channel 2",
        "d3": "Channel 3",
        "d4": "Channel 4",
        "d5": "Channel 5",
        "d6": "Channel 6",
        "delayed": "Ritardato",
        "dtcCodesAvailable": "DTC Code disponibile",
        "email to me": "E-Mail a me",
        "if": "Se",
        "inactive": "Inattivo",
        "inside": "All'interno di",
        "isOff": "è off",
        "isOn": "è on",
        "labelActivate": "Attivare la regola",
        "labelCopy": "Copiare la regola",
        "labelDeactivate": "Disattivare la regola",
        "labelDelete": "Eliminare la regola",
        "labelResolve": "Annullamento delle notifiche",
        "labelSnooze": "Regola di pausa",
        "labelUnsnooze": "Annulla la pausa",
        "locations": "{n} luogo | {n} luoghi",
        "maintenancePolicy": "La manutenzione è necessaria",
        "offlinefor": "Offline per {amount} {unit}",
        "onlineWithin": "Online nelle ultime {amount} {unit}",
        "outside": "All'esterno di",
        "regexMatches": "{0} matches {1}",
        "sms to me": "SMS a me",
        "snoozeOption1d": "1 giorno",
        "snoozeOption1h": "1 ore",
        "snoozeOption1w": "1 settimana",
        "snoozeOption4h": "4 ore",
        "snoozeOption5m": "5 minuti",
        "snoozedUntil": "Sonnecchiato fino a {0}",
        "then": "Poi",
        "timePrior": "tra {n} ora | tra {n} ore",
        "types": "{n} tipo | {n} tipi"
    }
}
</i18n>

<style lang="scss" scoped>
.notification-details-card {
    padding: 24px 28px 16px;
    background-color: #fff;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    font-size: 14px;
    color: $color-text-new;

    &__info {
        flex: 1;
    }

    &__description {
        i {
            margin-right: 0.1rem;
        }
    }

    &__actions {
        flex: 0;
        font-size: $fs-smaller;
        color: $color-primary;
        letter-spacing: -0.25px;

        & > * {
            text-align: center;

            label {
                margin-bottom: 6px;
            }
        }

        svg {
            color: $color-primary-light;
        }
    }

    h2 {
        flex-wrap: wrap;
        font-weight: 700;
        font-size: 16px;
        color: $color-text-new;

        & > * {
            margin-right: 8px;
        }
    }

    @include respond-to('for-mobile-down') {
        flex-wrap: wrap;

        &__actions {
            flex: 1 0 100%;
            margin: 16px 0 0;
            justify-content: flex-end;

            & > * {
                flex: 0;

                &:not(:first-child) {
                    margin-left: 16px;
                }
            }
        }
    }

    @include respond-to('for-small-mobile-only') {
        &__info {
            flex: 1 0 100%;
            margin: 12px 0 8px;
        }

        &__actions {
            justify-content: space-between;

            & > * {
                &:not(:first-child) {
                    margin: 0;
                }
            }
        }
    }
}
</style>
